import {
  optionRangeValues,
  questionHasOptions,
  rangeColumnKey,
  type FormQuestionOptionSchema,
  type FormQuestionSchema,
} from '../../../api/formSampling';

export type MatrixColumn = {
  key: string;
  title: string;
  rangeTitle?: string;
};

export type FormQuestionRenderBlock =
  | { kind: 'linear'; question: FormQuestionSchema }
  | { kind: 'matrix'; questions: FormQuestionSchema[]; columns: MatrixColumn[] };

export function optionTitleKey(title: string): string {
  return title.trim().toLocaleLowerCase('id');
}

export function questionOptionColumns(question: FormQuestionSchema): MatrixColumn[] {
  const seen = new Set<string>();
  const columns: MatrixColumn[] = [];
  for (const opt of question.options) {
    const range = optionRangeValues(opt);
    if (range.length) {
      const rangeTitle = opt.title.trim();
      for (const n of range) {
        const key = rangeColumnKey(opt.documentId, n);
        if (seen.has(key)) continue;
        seen.add(key);
        columns.push({ key, title: String(n), rangeTitle });
      }
      continue;
    }
    const key = optionTitleKey(opt.title);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    columns.push({ key, title: opt.title.trim() });
  }
  return columns;
}

export function optionColumnsSignature(columns: MatrixColumn[]): string {
  return columns.map((c) => c.key).join('\0');
}

export function matrixRangeCaption(columns: MatrixColumn[]): string {
  const titles = [...new Set(columns.map((c) => (c.rangeTitle ?? '').trim()).filter(Boolean))];
  return titles.length === 1 && columns.every((c) => c.rangeTitle) ? titles[0]! : '';
}

export function findQuestionOptionForColumn(
  question: FormQuestionSchema,
  columnKey: string,
): FormQuestionOptionSchema | undefined {
  const hash = columnKey.lastIndexOf('#');
  if (hash > 0) {
    const optionId = columnKey.slice(0, hash);
    return question.options.find((opt) => opt.documentId === optionId);
  }
  return question.options.find((opt) => optionTitleKey(opt.title) === columnKey);
}

export function groupQuestionsForRender(questions: FormQuestionSchema[]): FormQuestionRenderBlock[] {
  const blocks: FormQuestionRenderBlock[] = [];
  for (const question of questions) {
    if (!questionHasOptions(question)) {
      blocks.push({ kind: 'linear', question });
      continue;
    }
    const columns = questionOptionColumns(question);
    const last = blocks[blocks.length - 1];
    if (
      last?.kind === 'matrix' &&
      optionColumnsSignature(last.columns) === optionColumnsSignature(columns)
    ) {
      last.questions.push(question);
    } else {
      blocks.push({ kind: 'matrix', questions: [question], columns });
    }
  }
  return blocks;
}
